import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import { CalendarDays, Send, CheckCircle, CheckCircle2, CreditCard, Shield, Loader2, Users, PartyPopper, Info, Flame, FileText, ChefHat, TrendingDown, ChevronDown, ChevronUp, SlidersHorizontal, Zap, Egg } from 'lucide-react';
import { format, differenceInDays } from 'date-fns';
import { fr } from 'date-fns/locale';
import axios from 'axios';
import { trackEvent } from '@/lib/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Map accommodation type to media page name for preview image
const ACC_TO_MEDIA_PAGE = {
  'appartement-1': 'appartement-1',
  'appartement-2': 'appartement-2',
  'gite-seul': 'gite',
  'gite-salle': 'salle',
  'salle-seule': 'salle',
};

const ACCOMMODATION_OPTIONS = [
  { value: 'appartement-1', label: "L'Appartement Terrasse", maxG: 8 },
  { value: 'appartement-2', label: "L'Appartement Panorama", maxG: 8 },
  { value: 'gite-seul', label: 'Gite seul (sans salle)', maxG: 20 },
  { value: 'gite-salle', label: 'Gite + Salle des fetes', maxG: 20 },
];

// Events on gite-salle that are actually "salle only" (no gite included) —
// we auto-coerce the accommodation_type to salle-seule on submit.
const SALLE_SEULE_EVENTS = ['seminaire_jour', 'anniversaire_salle'];

const EVENT_OPTIONS = [
  { value: 'mariage', label: 'Mariage' },
  { value: 'seminaire_jour', label: 'Seminaire (journee seule)' },
  { value: 'seminaire_hebergement', label: 'Seminaire (avec hebergement)' },
  { value: 'anniversaire_salle', label: 'Anniversaire / Fete (salle seule)' },
  { value: 'anniversaire_gite_salle', label: 'Anniversaire / Fete (gite + salle)' },
];

const DAY_EVENTS = ['seminaire_jour', 'anniversaire_salle'];
const NEEDS_RECEPTION = ['mariage', 'seminaire_hebergement', 'anniversaire_gite_salle'];

const CAUTION_INFO = { 'appartement-1': 500, 'appartement-2': 500, 'gite-seul': 1000, 'gite-salle': 2500, 'salle-seule': 2500 };

const toDateStr = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;

const MONTH_SHORT = ['jan','fev','mars','avr','mai','juin','juil','aout','sept','oct','nov','dec'];

function formatMonthList(months) {
  if (!months || months.length === 0) return '';
  const sorted = [...months].sort((a, b) => a - b);
  return sorted.map(m => MONTH_SHORT[m - 1]).join(', ');
}

function isReveillon(d) { return d.getMonth() === 11 && (d.getDate() === 24 || d.getDate() === 31); }

export default function BookingForm() {
  const [ref, isVisible] = useScrollAnimation(0.1);
  const featureFlags = useFeatureFlags();
  const availableAccommodations = useMemo(
    () => ACCOMMODATION_OPTIONS.filter(o => o.value !== 'gite-salle' || featureFlags.gite_salle_enabled),
    [featureFlags.gite_salle_enabled],
  );
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', message: '',
    accommodation_type: '', event_type: '',
    adults: '2', children: '0', guests_reception: '',
  });
  const [options, setOptions] = useState({ bois: false, sono: false, vaisselle: false, cuisine: false, recharge: false, oeufs_qty: 0, selected_extras: [] });
  const [arrivalDate, setArrivalDate] = useState(null);
  const [departureDate, setDepartureDate] = useState(null);
  const [arrivalOpen, setArrivalOpen] = useState(false);
  const [departureOpen, setDepartureOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [checkoutUrl, setCheckoutUrl] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [pricingLoading, setPricingLoading] = useState(false);
  const [cgvAccepted, setCgvAccepted] = useState(false);
  const [cancelAccepted, setCancelAccepted] = useState(false);
  const [formulaImage, setFormulaImage] = useState(null);
  const [kitchenPrice, setKitchenPrice] = useState(300);
  const [availableExtras, setAvailableExtras] = useState([]);
  const debounceRef = useRef(null);
  const formTopRef = useRef(null);
  const pricingPanelRef = useRef(null);
  const bottomObsRef = useRef(null);
  const [stickyVisible, setStickyVisible] = useState(false);
  const [showMoreOptions, setShowMoreOptions] = useState(false);
  const [bookedNights, setBookedNights] = useState(new Set());
  const [optionAvailability, setOptionAvailability] = useState({ bois: true, sono: true, vaisselle: true, cuisine: true, recharge: true, oeufs: true, recharge_price_per_night: 5, eggs_price_per_unit: 1, eggs_max_quantity: 150 });
  const [seasonConfig, setSeasonConfig] = useState({ haute: [5,6,7,8,9], intermediaire: [4,10], basse: [1,2,3,11,12] });

  const getSeason = useCallback((month) => {
    for (const [season, months] of Object.entries(seasonConfig)) {
      if (months.includes(month)) return season;
    }
    return 'basse';
  }, [seasonConfig]);

  const rulesInfo = useMemo(() => {
    const basseMois = formatMonthList(seasonConfig.basse);
    const hauteMois = formatMonthList(seasonConfig.haute);
    return {
      'appartement-1': `Basse saison (${basseMois}) | Lun→Ven | Max 4 nuits | Menage+linge 80 EUR inclus`,
      'appartement-2': `Basse saison (${basseMois}) | Lun→Ven | Max 4 nuits | Menage+linge 80 EUR inclus`,
      'gite-seul': `Haute saison (${hauteMois}): lun-ven | Autres saisons: tous les jours | Menage+linge 150 EUR inclus`,
      'gite-salle': 'Ideal pour vos mariages, anniversaires, seminaires... et vos reveillons de fin d\'annee !',
    };
  }, [seasonConfig]);

  const accType = formData.accommodation_type;
  const evType = formData.event_type;
  const isApt = accType === 'appartement-1' || accType === 'appartement-2';
  const isGiteSeul = accType === 'gite-seul';
  const isGiteSalle = accType === 'gite-salle';
  const isDayEvent = DAY_EVENTS.includes(evType);
  const needsReception = NEEDS_RECEPTION.includes(evType);
  const maxLodging = isApt ? 8 : (evType === 'seminaire_hebergement' ? 22 : 20);
  const guestsLodging = (parseInt(formData.adults) || 0) + (parseInt(formData.children) || 0);

  const adultOptions = useMemo(() => Array.from({ length: maxLodging }, (_, i) => i + 1), [maxLodging]);
  const childOptions = useMemo(() => Array.from({ length: maxLodging }, (_, i) => i), [maxLodging]);
  const receptionOptions = useMemo(() => Array.from({ length: 150 }, (_, i) => i + 1), []);

  // Fetch dynamic preview image based on selected formula
  useEffect(() => {
    const pageName = ACC_TO_MEDIA_PAGE[accType];
    if (!pageName) { setFormulaImage(null); return; }
    axios.get(`${BACKEND_URL}/api/media/page/${pageName}`)
      .then(r => {
        const images = (r.data || []).filter(m => m.type === 'image');
        if (images.length > 0) {
          const f = images[0].files || {};
          const filename = f.desktop || f.mobile || f.thumb;
          if (filename) setFormulaImage(`${BACKEND_URL}/api/media/file/${filename}`);
          else setFormulaImage(null);
        } else {
          setFormulaImage(null);
        }
      })
      .catch(() => setFormulaImage(null));
  }, [accType]);

  // Fetch kitchen price
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/kitchen-price`)
      .then(r => setKitchenPrice(r.data?.cuisine_per_day || 300))
      .catch(() => {});
  }, []);

  // Fetch available extras
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/extras`)
      .then(r => setAvailableExtras(r.data || []))
      .catch(() => {});
  }, []);

  // Fetch option availability
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/option-availability`)
      .then(r => {
        const avail = r.data || {};
        setOptionAvailability(avail);
        // Uncheck any disabled options that were previously selected
        setOptions(prev => ({
          ...prev,
          bois: avail.bois === false ? false : prev.bois,
          sono: avail.sono === false ? false : prev.sono,
          vaisselle: avail.vaisselle === false ? false : prev.vaisselle,
          cuisine: avail.cuisine === false ? false : prev.cuisine,
        }));
      })
      .catch(() => {});
  }, []);

  // Fetch season config
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/season-config`)
      .then(r => { if (r.data) setSeasonConfig(r.data); })
      .catch(() => {});
  }, []);


  // Fetch blocked dates and compute booked nights per accommodation type
  useEffect(() => {
    if (!accType) { setBookedNights(new Set()); return; }
    axios.get(`${BACKEND_URL}/api/availability`)
      .then(r => {
        const blocks = (r.data || []).filter(b => b.accommodation_type === accType);
        const nights = new Set();
        blocks.forEach(b => {
          const start = new Date(b.start_date + 'T00:00:00');
          const end = new Date(b.end_date + 'T00:00:00');
          const d = new Date(start);
          while (d < end) {
            nights.add(toDateStr(d));
            d.setDate(d.getDate() + 1);
          }
        });
        setBookedNights(nights);
      })
      .catch(() => setBookedNights(new Set()));
  }, [accType]);

  // Arrival date disabled logic
  const isArrivalDisabled = useCallback((date) => {
    if (date < new Date(new Date().setHours(0, 0, 0, 0))) return true;
    if (bookedNights.has(toDateStr(date))) return true;
    if (!accType) return false;
    const m = date.getMonth() + 1, season = getSeason(m), wd = date.getDay();
    if (isApt) {
      if (season !== 'basse') return true;
      if (wd !== 1) return true;
      for (let i = 0; i < 5; i++) { const d = new Date(date); d.setDate(d.getDate() + i); if (isReveillon(d)) return true; }
    }
    if (isGiteSeul) {
      if (isReveillon(date)) return true;
      if (season === 'haute' && wd !== 1) return true;
    }
    return false;
  }, [accType, isApt, isGiteSeul, bookedNights, getSeason]);

  const isDepartureDisabled = useCallback((date) => {
    if (!arrivalDate) return true;
    if (date <= arrivalDate) return true;
    // Check if any night between arrival and departure is already booked
    const d = new Date(arrivalDate);
    while (d < date) {
      if (bookedNights.has(toDateStr(d))) return true;
      d.setDate(d.getDate() + 1);
    }
    if (!accType) return false;
    const wd = date.getDay(), arrSeason = getSeason(arrivalDate.getMonth() + 1);
    if (isApt) {
      if (wd === 0 || wd === 6 || wd === 1) return true;
      if (getSeason(date.getMonth() + 1) !== 'basse') return true;
      const t = new Date(arrivalDate);
      while (t < date) { if (isReveillon(t)) return true; t.setDate(t.getDate() + 1); }
    }
    if (isGiteSeul && arrSeason === 'haute') {
      if (wd === 0 || wd === 6 || wd === 1) return true;
      const t = new Date(arrivalDate);
      while (t < date) { if (isReveillon(t)) return true; t.setDate(t.getDate() + 1); }
    }
    return false;
  }, [accType, arrivalDate, isApt, isGiteSeul, bookedNights, getSeason]);

  const handleChange = (field, value) => {
    setFormData(prev => {
      const n = { ...prev, [field]: value };
      if (field === 'accommodation_type') {
        n.event_type = '';
        n.guests_reception = '';
        const mx = value.startsWith('appartement') ? 8 : 20;
        if ((parseInt(n.adults) || 0) + (parseInt(n.children) || 0) > mx) { n.adults = String(mx); n.children = '0'; }
      }
      if (field === 'event_type') n.guests_reception = '';
      // Cap total guests to max lodging
      if (field === 'adults' || field === 'children') {
        const mx = prev.accommodation_type.startsWith('appartement') ? 8 : 20;
        const a = parseInt(field === 'adults' ? value : n.adults) || 0;
        const c = parseInt(field === 'children' ? value : n.children) || 0;
        if (a + c > mx) {
          if (field === 'adults') n.children = String(Math.max(0, mx - a));
          else n.adults = String(Math.max(1, mx - c));
        }
      }
      return n;
    });
    if (field === 'accommodation_type') { setArrivalDate(null); setDepartureDate(null); setOptions({ bois: false, sono: false, vaisselle: false, cuisine: false, recharge: false, oeufs_qty: 0, selected_extras: [] }); }
  };

  const toggleOption = (key) => {
    setOptions(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Auto-calculate price with debounce using lightweight /api/simulate-price
  const canSimulate = accType && (isDayEvent || (arrivalDate && departureDate)) && (!isGiteSalle || evType);

  useEffect(() => {
    if (!canSimulate) { setPricing(null); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setPricingLoading(true);
    debounceRef.current = setTimeout(async () => {
      const arrStr = arrivalDate ? format(arrivalDate, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd');
      const depStr = isDayEvent ? arrStr : (departureDate ? format(departureDate, 'yyyy-MM-dd') : arrStr);
      // Coerce accommodation_type to salle-seule for events that don't include the gite
      const effectiveAcc = (isGiteSalle && SALLE_SEULE_EVENTS.includes(evType)) ? 'salle-seule' : accType;
      try {
        const res = await axios.post(`${BACKEND_URL}/api/simulate-price`, {
          accommodation_type: effectiveAcc,
          arrival_date: arrStr,
          departure_date: depStr,
          guests_lodging: guestsLodging || 1,
          adults: parseInt(formData.adults) || 1,
          children: parseInt(formData.children) || 0,
          guests_reception: formData.guests_reception ? parseInt(formData.guests_reception) : null,
          event_type: isGiteSalle ? evType : null,
          options,
        });
        setPricing(res.data);
        trackEvent('devis_calcule', {
          accommodation_type: effectiveAcc,
          value: res.data?.total || 0,
          currency: 'EUR',
        });
      } catch {
        setPricing(null);
      }
      setPricingLoading(false);
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [accType, arrivalDate, departureDate, formData.adults, formData.children, formData.guests_reception, evType, options, isGiteSalle, isDayEvent, canSimulate, guestsLodging]);

  // Sticky bar: show when form top leaves viewport, hide when pricing panel is visible
  const stickyStateRef = useRef({ topOut: false, bottomIn: false });
  useEffect(() => {
    const topEl = formTopRef.current;
    if (!topEl) return;
    const calc = () => setStickyVisible(stickyStateRef.current.topOut && !stickyStateRef.current.bottomIn);
    const topObs = new IntersectionObserver(([e]) => {
      stickyStateRef.current.topOut = !e.isIntersecting;
      calc();
    }, { threshold: 0 });
    topObs.observe(topEl);
    return () => topObs.disconnect();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Observe pricing panel separately (it renders conditionally)
  const pricingRefCallback = useCallback((node) => {
    pricingPanelRef.current = node;
    if (bottomObsRef.current) { bottomObsRef.current.disconnect(); bottomObsRef.current = null; }
    if (node) {
      const obs = new IntersectionObserver(([e]) => {
        stickyStateRef.current.bottomIn = e.isIntersecting;
        setStickyVisible(stickyStateRef.current.topOut && !stickyStateRef.current.bottomIn);
      }, { threshold: 0.1 });
      obs.observe(node);
      bottomObsRef.current = obs;
    } else {
      stickyStateRef.current.bottomIn = false;
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const accLabel = ACCOMMODATION_OPTIONS.find(o => o.value === accType)?.label;
  const evLabel = evType ? EVENT_OPTIONS.find(o => o.value === evType)?.label : null;
  const nights = arrivalDate && departureDate ? differenceInDays(departureDate, arrivalDate) : 0;
  const activeOpts = [
    options.bois && 'Bois',
    options.sono && 'Sono',
    options.vaisselle && 'Location vaisselle',
    options.cuisine && 'Location cuisine',
    options.recharge && 'Recharge',
  ].filter(Boolean);

  const scrollToPayment = () => {
    const el = document.querySelector('[data-testid="pricing-breakdown"]') || document.querySelector('[data-testid="booking-submit-btn"]');
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setCheckoutUrl(null);

    if (!formData.name || !formData.email || !formData.phone || !accType) {
      setError('Veuillez remplir tous les champs obligatoires.');
      return;
    }
    if (!isDayEvent && (!arrivalDate || !departureDate)) {
      setError('Veuillez selectionner vos dates.');
      return;
    }
    if (isGiteSalle && !evType) {
      setError("Veuillez choisir un type d'evenement.");
      return;
    }
    if (needsReception && !formData.guests_reception) {
      setError("Veuillez indiquer le nombre d'invites.");
      return;
    }
    if (!pricing) {
      setError('Le tarif n\'a pas pu etre calcule. Veuillez verifier vos parametres.');
      return;
    }
    if (!cgvAccepted || !cancelAccepted) {
      setError('Veuillez accepter les CGV et les conditions d\'annulation.');
      return;
    }

    setLoading(true);
    try {
      const arrStr = arrivalDate ? format(arrivalDate, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd');
      const depStr = isDayEvent ? arrStr : format(departureDate, 'yyyy-MM-dd');
      // Coerce accommodation_type to salle-seule for events that don't include the gite (Phase 85)
      const effectiveAcc = (isGiteSalle && SALLE_SEULE_EVENTS.includes(evType)) ? 'salle-seule' : accType;
      const payload = {
        name: formData.name, email: formData.email, phone: formData.phone,
        accommodation_type: effectiveAcc,
        arrival_date: arrStr,
        departure_date: depStr,
        guests: guestsLodging || 1,
        guests_lodging: guestsLodging || 1,
        adults: parseInt(formData.adults) || 1,
        children: parseInt(formData.children) || 0,
        guests_reception: formData.guests_reception ? parseInt(formData.guests_reception) : null,
        event_type: isGiteSalle ? evType : null,
        options: isApt ? null : options,
        message: formData.message,
        cgv_accepted: cgvAccepted,
        cancellation_policy_accepted: cancelAccepted,
      };

      // Step 1: Create reservation
      let reservationId;
      try {
        const resReservation = await axios.post(`${BACKEND_URL}/api/reservations`, payload);
        reservationId = resReservation.data.id;
        if (!reservationId) throw new Error('ID de reservation manquant');
        trackEvent('devis_envoye', {
          accommodation_type: effectiveAcc,
          value: resReservation.data?.amount || 0,
          currency: 'EUR',
        });
      } catch (err) {
        const msg = err.response?.data?.detail || err.message || 'Erreur lors de la creation de la reservation.';
        setError(msg);
        setLoading(false);
        return;
      }

      // Step 2: Create Stripe checkout session
      let stripeUrl;
      try {
        const resCheckout = await axios.post(`${BACKEND_URL}/api/payments/checkout`, {
          reservation_id: reservationId,
          origin_url: window.location.origin,
          payment_type: 'deposit',
        });
        stripeUrl = resCheckout.data?.url;
        if (!stripeUrl) throw new Error('URL de paiement manquante');
      } catch (err) {
        const msg = err.response?.data?.detail || err.message || 'Erreur lors de la creation de la session de paiement.';
        setError(msg);
        setLoading(false);
        return;
      }

      // Step 3: Show payment button - do NOT rely on automatic redirect
      // (AdBlock and privacy browsers block window.location redirects to stripe.com)
      setCheckoutUrl(stripeUrl);
      setLoading(false);
    } catch (err) {
      setError(err.message || 'Une erreur inattendue est survenue.');
      setLoading(false);
    }
  };

  const fmtEUR = (v) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(v);

  if (success) {
    return (
      <section id="reservation" data-testid="booking-section" className="py-24 md:py-32 noise-bg" style={{ scrollMarginTop: '120px' }}>
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="max-w-xl mx-auto text-center">
            <CheckCircle className="w-16 h-16 text-[#8A9A5B] mx-auto mb-6" strokeWidth={1.5} />
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#2C3E30] mb-4">Demande envoyee !</h2>
            <p className="text-[#595959] mb-8">Merci pour votre demande. Nous vous recontacterons rapidement.</p>
            <Button data-testid="booking-new-request-btn" onClick={() => setSuccess(false)} className="rounded-full px-8 bg-[#2C3E30] text-[#F2F0E9] hover:bg-[#3a5040]">Nouvelle demande</Button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <>
      {/* ── Sticky summary bar (desktop + tablet) ── */}
      {stickyVisible && accType && (
        <div
          data-testid="sticky-summary-bar"
          className="fixed top-14 left-0 right-0 z-[45] hidden md:block"
          style={{ animation: 'slideDown 0.3s ease-out' }}
        >
          <div className="bg-[#2C3E30] shadow-[0_4px_20px_-2px_rgba(0,0,0,0.25)]">
            <div className="max-w-[1400px] mx-auto px-6 flex items-center gap-5 h-12">
              {/* Formule */}
              <div className="flex items-center gap-1.5 min-w-0">
                <div className="w-2 h-2 rounded-full bg-[#C4A962] flex-shrink-0" />
                <span className="text-sm font-semibold text-[#F2F0E9] truncate" data-testid="sticky-formula">
                  {accLabel}{evLabel ? ` — ${evLabel}` : ''}
                </span>
              </div>

              <div className="w-px h-5 bg-white/20 flex-shrink-0" />

              {/* Dates */}
              {arrivalDate && (
                <>
                  <div className="flex items-center gap-1.5 text-sm text-[#F2F0E9]/80 flex-shrink-0">
                    <CalendarDays className="w-3.5 h-3.5 text-[#C4A962]" strokeWidth={1.5} />
                    <span>{format(arrivalDate, 'dd MMM', { locale: fr })}</span>
                    {departureDate && <span>→ {format(departureDate, 'dd MMM', { locale: fr })}</span>}
                    {nights > 0 && <span className="text-[#C4A962] font-medium">({nights}n)</span>}
                  </div>
                  <div className="w-px h-5 bg-white/20 flex-shrink-0" />
                </>
              )}

              {/* Personnes */}
              <div className="flex items-center gap-1 text-sm text-[#F2F0E9]/80 flex-shrink-0">
                <Users className="w-3.5 h-3.5 text-[#C4A962]" strokeWidth={1.5} />
                <span>{guestsLodging} pers.</span>
                {needsReception && formData.guests_reception && <span>+ {formData.guests_reception} inv.</span>}
              </div>

              {/* Options */}
              {activeOpts.length > 0 && (
                <>
                  <div className="w-px h-5 bg-white/20 flex-shrink-0" />
                  <div className="flex items-center gap-1 text-xs flex-shrink-0">
                    {activeOpts.map(o => (
                      <span key={o} className="px-1.5 py-0.5 rounded bg-[#C4A962]/20 text-[#C4A962] font-medium">{o}</span>
                    ))}
                  </div>
                </>
              )}

              {/* Spacer */}
              <div className="flex-1" />

              {/* Total + CTA */}
              {pricing ? (
                <div className="flex items-center gap-4 flex-shrink-0">
                  <div className="text-right">
                    <p className="text-[10px] text-[#F2F0E9]/60 leading-none">Total</p>
                    <p className="text-lg font-bold text-[#F2F0E9] leading-tight" data-testid="sticky-total">{fmtEUR(pricing.total)}</p>
                  </div>
                  <button
                    type="button"
                    onClick={scrollToPayment}
                    className="flex items-center gap-1.5 px-5 py-2 rounded-full bg-[#C4A962] text-[#2C3E30] text-sm font-semibold hover:bg-[#d4b972] transition-colors shadow-sm"
                    data-testid="sticky-reserve-btn"
                  >
                    Reserver <ChevronDown className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : pricingLoading ? (
                <div className="flex items-center gap-2 text-sm text-[#F2F0E9]/70 flex-shrink-0">
                  <Loader2 className="w-4 h-4 animate-spin text-[#C4A962]" />
                  <span>Calcul...</span>
                </div>
              ) : (
                <span className="text-xs text-[#F2F0E9]/50 italic flex-shrink-0">Selectionnez vos dates</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Mobile bottom bar ── */}
      {stickyVisible && accType && (
        <div
          data-testid="mobile-bottom-bar"
          className="fixed bottom-0 left-0 right-0 z-[45] md:hidden"
          style={{ animation: 'slideUp 0.3s ease-out' }}
        >
          <div className="bg-[#2C3E30] shadow-[0_-4px_20px_-2px_rgba(0,0,0,0.25)] px-4 py-3 safe-area-bottom">
            <div className="flex items-center justify-between gap-3">
              <div className="flex-1 min-w-0">
                {pricing ? (
                  <div>
                    <p className="text-[10px] text-[#F2F0E9]/60 leading-none">Total sejour</p>
                    <p className="text-xl font-bold text-[#F2F0E9] leading-tight" data-testid="mobile-total">{fmtEUR(pricing.total)}</p>
                  </div>
                ) : pricingLoading ? (
                  <div className="flex items-center gap-2 text-sm text-[#F2F0E9]/70">
                    <Loader2 className="w-4 h-4 animate-spin text-[#C4A962]" />
                    <span>Calcul...</span>
                  </div>
                ) : (
                  <p className="text-xs text-[#F2F0E9]/50">{accLabel}</p>
                )}
              </div>
              <button
                type="button"
                onClick={scrollToPayment}
                className="flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-[#C4A962] text-[#2C3E30] text-sm font-semibold hover:bg-[#d4b972] transition-colors shadow-md flex-shrink-0"
                data-testid="mobile-reserve-btn"
              >
                Reserver <ChevronDown className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      )}

      <section id="reservation" data-testid="booking-section" className="py-16 md:py-24 lg:py-32 pb-32 md:pb-24 lg:pb-32 noise-bg" style={{ scrollMarginTop: '120px' }}>
      <div ref={ref} className={`max-w-[1400px] mx-auto px-4 md:px-12 fade-in-section ${isVisible ? 'is-visible' : ''}`}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20">
          {/* Left info - hidden on mobile */}
          <div className="hidden lg:flex flex-col justify-center">
            <span className="text-[#8A9A5B] text-sm font-medium tracking-[0.2em] uppercase">Reservez maintenant</span>
            <h2 data-testid="booking-title" className="font-['Playfair_Display'] text-3xl md:text-5xl text-[#2C3E30] mt-3 tracking-tight">
              Votre sejour<br /><span className="italic font-normal">vous attend</span>
            </h2>
            <p className="mt-6 text-[#595959] text-base md:text-lg leading-relaxed">
              Remplissez ce formulaire pour obtenir un devis instantane et reserver en ligne.
            </p>
            <div className="mt-8 space-y-4">
              {['Choisissez votre formule', 'Selectionnez vos dates et options', 'Payez votre acompte en ligne'].map((t, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#8A9A5B]/10 flex items-center justify-center text-[#8A9A5B] text-sm font-semibold">{i+1}</div>
                  <span className="text-[#1C1C1E] text-sm">{t}</span>
                </div>
              ))}
            </div>
            <div className="mt-10 rounded-xl overflow-hidden h-48 md:h-64 hidden lg:block">
              <img
                src={formulaImage || 'https://res.cloudinary.com/amenitiz/image/upload/q_auto,f_auto,w_800/v1702916835/z0os7w2naece1t5h1oz2.jpg'}
                alt={accType ? ACCOMMODATION_OPTIONS.find(o => o.value === accType)?.label || 'Le Domaine' : 'Le Domaine'}
                className="w-full h-full object-cover transition-all duration-500"
                loading="lazy"
                data-testid="booking-formula-image"
              />
            </div>
          </div>

          {/* Right form */}
          <div className="bg-white rounded-2xl p-5 md:p-8 lg:p-10 shadow-[0_4px_20px_-2px_rgba(0,0,0,0.05)]">
            {/* Mobile-only compact header */}
            <div className="lg:hidden mb-5">
              <h2 className="font-['Playfair_Display'] text-2xl text-[#2C3E30] tracking-tight">Reservez votre sejour</h2>
              <p className="text-sm text-[#595959] mt-1">Devis instantane et paiement securise</p>
            </div>
            <div ref={formTopRef} />
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* ── ETAPE 1 : FORMULE ── */}
              <div className="space-y-5">
              {/* 1. Accommodation */}
              <div>
                <label className="text-sm font-medium text-[#1C1C1E] mb-1.5 block">Formule *</label>
                <Select value={accType} onValueChange={(v) => handleChange('accommodation_type', v)}>
                  <SelectTrigger data-testid="booking-accommodation-select" className="rounded-lg h-11"><SelectValue placeholder="Choisir une formule" /></SelectTrigger>
                  <SelectContent>
                    {availableAccommodations.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                {accType && <div data-testid="rules-info" className="mt-2 flex items-start gap-2 p-3 rounded-lg bg-[#F5F5F0] text-xs text-[#595959]"><Info className="w-3.5 h-3.5 mt-0.5 text-[#8A9A5B] flex-shrink-0" strokeWidth={1.5} /><span>{rulesInfo[accType]}</span></div>}
              </div>

              {/* 2. Event type (gite-salle only) */}
              {isGiteSalle && (
                <div>
                  <label className="text-sm font-medium text-[#1C1C1E] mb-1.5 flex items-center gap-1.5"><PartyPopper className="w-3.5 h-3.5 text-[#C4A962]" strokeWidth={1.5} /> Type d'evenement *</label>
                  <Select value={evType} onValueChange={(v) => handleChange('event_type', v)}>
                    <SelectTrigger data-testid="booking-event-type-select" className="rounded-lg h-11"><SelectValue placeholder="Choisir un evenement" /></SelectTrigger>
                    <SelectContent>
                      {EVENT_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* 3. Dates (skip for day events) */}
              {!isDayEvent && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-[#1C1C1E] mb-1.5 block">Arrivee *</label>
                    <Popover open={arrivalOpen} onOpenChange={setArrivalOpen}>
                      <PopoverTrigger asChild>
                        <Button data-testid="booking-arrival-date" variant="outline" className="w-full justify-start text-left font-normal rounded-lg h-11">
                          <CalendarDays className="w-4 h-4 mr-2 text-[#8A9A5B]" strokeWidth={1.5} />
                          {arrivalDate ? format(arrivalDate, 'dd MMM yyyy', { locale: fr }) : "Date d'arrivee"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start"><Calendar mode="single" selected={arrivalDate} onSelect={(d) => { setArrivalDate(d); setDepartureDate(null); setArrivalOpen(false); }} disabled={isArrivalDisabled} defaultMonth={arrivalDate || undefined} /></PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#1C1C1E] mb-1.5 block">Depart *</label>
                    <Popover open={departureOpen} onOpenChange={setDepartureOpen}>
                      <PopoverTrigger asChild>
                        <Button data-testid="booking-departure-date" variant="outline" className="w-full justify-start text-left font-normal rounded-lg h-11">
                          <CalendarDays className="w-4 h-4 mr-2 text-[#8A9A5B]" strokeWidth={1.5} />
                          {departureDate ? format(departureDate, 'dd MMM yyyy', { locale: fr }) : 'Date de depart'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start"><Calendar mode="single" selected={departureDate} onSelect={(d) => { setDepartureDate(d); setDepartureOpen(false); }} disabled={isDepartureDisabled} defaultMonth={departureDate || arrivalDate || undefined} /></PopoverContent>
                    </Popover>
                  </div>
                </div>
              )}

              {/* Date for day events */}
              {isDayEvent && (
                <div>
                  <label className="text-sm font-medium text-[#1C1C1E] mb-1.5 block">Date de l'evenement *</label>
                  <Popover open={arrivalOpen} onOpenChange={setArrivalOpen}>
                    <PopoverTrigger asChild>
                      <Button data-testid="booking-arrival-date" variant="outline" className="w-full justify-start text-left font-normal rounded-lg h-11">
                        <CalendarDays className="w-4 h-4 mr-2 text-[#8A9A5B]" strokeWidth={1.5} />
                        {arrivalDate ? format(arrivalDate, 'dd MMM yyyy', { locale: fr }) : "Choisir une date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start"><Calendar mode="single" selected={arrivalDate} onSelect={(d) => { setArrivalDate(d); setDepartureDate(d); setArrivalOpen(false); }} disabled={(date) => date < new Date(new Date().setHours(0,0,0,0)) || bookedNights.has(toDateStr(date))} defaultMonth={arrivalDate || undefined} /></PopoverContent>
                  </Popover>
                </div>
              )}

              {/* 4. Guests */}
              <div className={!isDayEvent ? 'grid grid-cols-2 sm:grid-cols-3 gap-4' : (needsReception || (isGiteSalle && evType === 'seminaire_jour') ? 'grid grid-cols-1 sm:grid-cols-2 gap-4' : '')}>
                {!isDayEvent && (
                  <>
                    <div>
                      <label className="text-sm font-medium text-[#1C1C1E] mb-1.5 flex items-center gap-1.5"><Users className="w-3.5 h-3.5 text-[#8A9A5B]" strokeWidth={1.5} /> Adultes *</label>
                      <Select value={formData.adults} onValueChange={(v) => { handleChange('adults', v); }}>
                        <SelectTrigger data-testid="booking-adults-select" className="rounded-lg h-11"><SelectValue /></SelectTrigger>
                        <SelectContent>{adultOptions.map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-[#1C1C1E] mb-1.5 block">Enfants (-18 ans)</label>
                      <Select value={formData.children} onValueChange={(v) => { handleChange('children', v); }}>
                        <SelectTrigger data-testid="booking-children-select" className="rounded-lg h-11"><SelectValue /></SelectTrigger>
                        <SelectContent>{childOptions.map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </>
                )}
                {(needsReception || evType === 'seminaire_jour') && (
                  <div>
                    <label className="text-sm font-medium text-[#1C1C1E] mb-1.5 flex items-center gap-1.5"><PartyPopper className="w-3.5 h-3.5 text-[#C4A962]" strokeWidth={1.5} /> {isDayEvent ? 'Participants' : 'Invites reception'} *</label>
                    <Select value={formData.guests_reception} onValueChange={(v) => handleChange('guests_reception', v)}>
                      <SelectTrigger data-testid="booking-guests-reception-select" className="rounded-lg h-11"><SelectValue placeholder="Nombre" /></SelectTrigger>
                      <SelectContent>{receptionOptions.map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
                    </Select>
                    {needsReception && <p className="text-xs text-[#C4A962] mt-1">Dormeurs inclus dans les invites</p>}
                  </div>
                )}
              </div>
              </div>{/* fin etape 1 */}

              {/* ── ETAPE 2 : OPTIONS ── */}
              {(isGiteSeul || (isGiteSalle && evType)) && (
                <>
                  {/* Mobile: "Plus d'options" toggle */}
                  <button
                    type="button"
                    onClick={() => setShowMoreOptions(p => !p)}
                    className="lg:hidden w-full flex items-center justify-between p-3 rounded-xl border border-[#E5E5E0] bg-[#FAFAF9] text-sm font-medium text-[#2C3E30]"
                    data-testid="more-options-toggle"
                  >
                    <span className="flex items-center gap-2">
                      <SlidersHorizontal className="w-4 h-4 text-[#8A9A5B]" strokeWidth={1.5} />
                      Options et extras
                      {(options.bois || options.sono || options.vaisselle || options.cuisine || options.recharge || options.oeufs_qty > 0 || options.selected_extras.length > 0) && (
                        <span className="px-1.5 py-0.5 rounded-full bg-[#C4A962]/20 text-[#C4A962] text-xs font-semibold">{[options.bois, options.sono, options.vaisselle, options.cuisine, options.recharge, options.oeufs_qty > 0].filter(Boolean).length + options.selected_extras.length}</span>
                      )}
                    </span>
                    {showMoreOptions ? <ChevronUp className="w-4 h-4 text-[#595959]" /> : <ChevronDown className="w-4 h-4 text-[#595959]" />}
                  </button>
                  {/* Desktop: always show. Mobile: only when expanded */}
                  <div className={`${showMoreOptions ? 'block' : 'hidden'} lg:block space-y-4`}>
                    <div className="space-y-3.5 p-5 rounded-xl border border-[#E5E5E0] bg-[#FAFAF9]">
                      <p className="text-xs font-semibold text-[#8A9A5B] tracking-[0.1em] uppercase hidden lg:block">Options</p>
                  {(isGiteSeul || (isGiteSalle && evType !== 'seminaire_jour' && evType !== 'anniversaire_salle')) && (
                    <label className={`flex items-center gap-3 ${optionAvailability.bois ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}>
                      <Checkbox data-testid="option-bois" checked={options.bois} onCheckedChange={() => optionAvailability.bois && toggleOption('bois')} disabled={!optionAvailability.bois} className="border-[#8A9A5B] data-[state=checked]:bg-[#8A9A5B]" />
                      <Flame className="w-4 h-4 text-[#C4A962]" strokeWidth={1.5} />
                      <span className="text-sm text-[#2C3E30]">Bois cheminee (15 EUR/jour)</span>
                      {!optionAvailability.bois && <span className="text-[10px] text-red-400 font-medium ml-1">Indisponible</span>}
                    </label>
                  )}
                  {(evType === 'mariage' || evType === 'anniversaire_gite_salle' || evType === 'anniversaire_salle') && (
                    <>
                      <label className={`flex items-center gap-3 ${optionAvailability.sono ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}>
                        <Checkbox data-testid="option-sono" checked={options.sono} onCheckedChange={() => optionAvailability.sono && toggleOption('sono')} disabled={!optionAvailability.sono} className="border-[#8A9A5B] data-[state=checked]:bg-[#8A9A5B]" />
                        <span className="text-sm text-[#2C3E30]">Sono / DJ (500 EUR)</span>
                        {!optionAvailability.sono && <span className="text-[10px] text-red-400 font-medium ml-1">Indisponible</span>}
                      </label>
                      <label className={`flex items-center gap-3 ${optionAvailability.vaisselle ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}>
                        <Checkbox data-testid="option-vaisselle" checked={options.vaisselle} onCheckedChange={() => optionAvailability.vaisselle && toggleOption('vaisselle')} disabled={!optionAvailability.vaisselle} className="border-[#8A9A5B] data-[state=checked]:bg-[#8A9A5B]" />
                        <span className="text-sm text-[#2C3E30]">Location vaisselle (3 EUR/invite)</span>
                        {!optionAvailability.vaisselle && <span className="text-[10px] text-red-400 font-medium ml-1">Indisponible</span>}
                      </label>
                    </>
                  )}
                  {isGiteSalle && (
                    <>
                      <label className={`flex items-center gap-3 ${optionAvailability.cuisine ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}>
                        <Checkbox data-testid="option-cuisine" checked={options.cuisine} onCheckedChange={() => optionAvailability.cuisine && toggleOption('cuisine')} disabled={!optionAvailability.cuisine} className="border-[#C4A962] data-[state=checked]:bg-[#C4A962] data-[state=checked]:border-[#C4A962]" />
                        <ChefHat className="w-4 h-4 text-[#C4A962]" strokeWidth={1.5} />
                        <span className="text-sm text-[#2C3E30]">Location cuisine ({kitchenPrice + Math.max(5 * (parseInt(formData.guests_reception) || 0), 300)} EUR)</span>
                        {!optionAvailability.cuisine && <span className="text-[10px] text-red-400 font-medium ml-1">Indisponible</span>}
                      </label>
                      <a
                        href="/traiteurs-partenaires"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="caterers-link-from-booking"
                        className="ml-7 text-xs text-[#C4A962] hover:text-[#b5945a] underline underline-offset-2 inline-flex items-center gap-1"
                      >
                        <ChefHat className="w-3 h-3" strokeWidth={1.5} />
                        Voir nos traiteurs partenaires
                      </a>
                    </>
                  )}
                  {/* Recharge electrique - available for all accommodation types */}
                  <label className={`flex items-center gap-3 ${optionAvailability.recharge ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}>
                    <Checkbox data-testid="option-recharge" checked={options.recharge} onCheckedChange={() => optionAvailability.recharge && toggleOption('recharge')} disabled={!optionAvailability.recharge} className="border-[#8A9A5B] data-[state=checked]:bg-[#8A9A5B]" />
                    <Zap className="w-4 h-4 text-[#8A9A5B]" strokeWidth={1.5} />
                    <span className="text-sm text-[#2C3E30]">Recharge electrique ({optionAvailability.recharge_price_per_night || 5} EUR/nuit)</span>
                    {!optionAvailability.recharge && <span className="text-[10px] text-red-400 font-medium ml-1">Indisponible</span>}
                  </label>
                  {/* Œufs frais du domaine - vendus a l'unite (0 - max 150) */}
                  <div className={`${optionAvailability.oeufs ? '' : 'opacity-50'}`}>
                    <div className="flex items-center gap-3 flex-wrap">
                      <Egg className="w-4 h-4 text-[#8A9A5B]" strokeWidth={1.5} />
                      <span className="text-sm text-[#2C3E30]">
                        Oeufs frais du domaine
                        <span className="text-xs text-[#595959] ml-1">({optionAvailability.eggs_price_per_unit || 1} EUR/oeuf, max {optionAvailability.eggs_max_quantity || 150})</span>
                      </span>
                      <input
                        type="number"
                        min="0"
                        max={optionAvailability.eggs_max_quantity || 150}
                        step="1"
                        data-testid="option-oeufs-qty"
                        value={options.oeufs_qty || 0}
                        disabled={!optionAvailability.oeufs}
                        onChange={(e) => {
                          const raw = Number.parseInt(e.target.value, 10);
                          const max = optionAvailability.eggs_max_quantity || 150;
                          const qty = Number.isNaN(raw) ? 0 : Math.max(0, Math.min(raw, max));
                          setOptions(o => ({ ...o, oeufs_qty: qty }));
                        }}
                        className="ml-auto w-20 text-sm border border-[#E5E5E0] rounded px-2 py-1 text-center disabled:cursor-not-allowed"
                      />
                      {options.oeufs_qty > 0 && (
                        <span className="text-xs font-medium text-[#8A9A5B]">
                          = {(options.oeufs_qty * (optionAvailability.eggs_price_per_unit || 1)).toFixed(2)} EUR
                        </span>
                      )}
                      {!optionAvailability.oeufs && <span className="text-[10px] text-red-400 font-medium ml-1">Indisponible</span>}
                    </div>
                    {optionAvailability.oeufs && (
                      <p className="text-[11px] text-[#8A9A5B]/80 italic mt-1.5 ml-7" data-testid="oeufs-hint">
                        Astuce : 6 oeufs pour un petit-dejeuner, 12 pour une omelette geante, 30 pour un brunch entre amis ?
                      </p>
                    )}
                  </div>
                </div>

              {/* 5b. Custom extras (for gite formulas only) — inside collapsible on mobile */}
              {(() => {
                const effectiveTypes = [accType, evType].filter(Boolean);
                const filteredExtras = availableExtras.filter(ex =>
                  !ex.applicable_to?.length || ex.applicable_to.some(a => effectiveTypes.includes(a))
                );
                if (filteredExtras.length === 0) return null;
                return (
                  <div className="space-y-3.5 p-5 rounded-xl border border-[#C4A962]/20 bg-[#C4A962]/5">
                    <p className="text-xs font-semibold text-[#C4A962] tracking-[0.1em] uppercase">Extras</p>
                    {filteredExtras.map(ex => (
                      <label key={ex.id} className="flex items-start gap-3 cursor-pointer" data-testid={`extra-option-${ex.id}`}>
                        <Checkbox
                          checked={options.selected_extras.includes(ex.id)}
                          onCheckedChange={() => {
                            setOptions(o => ({
                              ...o,
                              selected_extras: o.selected_extras.includes(ex.id)
                                ? o.selected_extras.filter(i => i !== ex.id)
                                : [...o.selected_extras, ex.id],
                            }));
                          }}
                          className="border-[#C4A962] data-[state=checked]:bg-[#C4A962] data-[state=checked]:border-[#C4A962] mt-0.5"
                        />
                        {ex.image_url && (
                          <img
                            src={ex.image_url.startsWith('http') ? ex.image_url : `${BACKEND_URL}${ex.image_url}`}
                            alt={ex.title}
                            className="w-10 h-10 rounded-lg object-cover border border-[#E5E5E0] flex-shrink-0"
                          />
                        )}
                        <div>
                          <span className="text-sm text-[#2C3E30] font-medium">{ex.title} ({ex.price} EUR)</span>
                          {ex.description && <p className="text-xs text-[#595959] mt-0.5 line-clamp-2">{ex.description}</p>}
                        </div>
                      </label>
                    ))}
                  </div>
                );
              })()}
                  </div>{/* end collapsible wrapper */}
                </>
              )}

              {/* ── ETAPE 3 : COORDONNEES ── */}
              <div className="space-y-5">
              <p className="text-xs font-semibold text-[#8A9A5B] tracking-[0.1em] uppercase pt-1">Vos coordonnees</p>
              {/* 6. Contact */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-[#1C1C1E] mb-1.5 block">Nom complet *</label>
                  <Input data-testid="booking-name-input" value={formData.name} onChange={(e) => handleChange('name', e.target.value)} placeholder="Jean Dupont" className="rounded-lg h-11" />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#1C1C1E] mb-1.5 block">Telephone *</label>
                  <Input data-testid="booking-phone-input" value={formData.phone} onChange={(e) => handleChange('phone', e.target.value)} placeholder="06 12 34 56 78" className="rounded-lg h-11" />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-[#1C1C1E] mb-1.5 block">Email *</label>
                <Input data-testid="booking-email-input" type="email" value={formData.email} onChange={(e) => handleChange('email', e.target.value)} placeholder="jean@exemple.fr" className="rounded-lg h-11" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#1C1C1E] mb-1.5 block">Message (optionnel)</label>
                <Textarea data-testid="booking-message-input" value={formData.message} onChange={(e) => handleChange('message', e.target.value)} placeholder="Precisions..." className="rounded-lg min-h-[70px]" />
              </div>
              </div>{/* fin etape 3 */}

              {error && <p data-testid="booking-error" className="text-[#993333] text-sm">{error}</p>}

              {/* Payment button - shown after reservation is confirmed */}
              {checkoutUrl && (
                <div data-testid="stripe-redirect-fallback" ref={el => el?.scrollIntoView({ behavior: 'smooth', block: 'center' })} className="bg-white border-2 border-[#C4A962] rounded-2xl p-6 text-center space-y-4 shadow-lg animate-in fade-in slide-in-from-bottom-4 duration-500">
                  <div className="w-12 h-12 rounded-full bg-[#8A9A5B]/10 flex items-center justify-center mx-auto">
                    <CheckCircle2 className="w-6 h-6 text-[#8A9A5B]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-base font-semibold text-[#2C3E30]">Reservation confirmee !</p>
                    <p className="text-sm text-[#595959] mt-1">Cliquez ci-dessous pour proceder au paiement securise de votre acompte.</p>
                  </div>
                  <a
                    href={checkoutUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="stripe-redirect-link"
                    className="inline-flex items-center justify-center gap-2 bg-[#C4A962] text-white font-semibold text-base px-8 py-4 rounded-full hover:bg-[#b5945a] transition-all hover:scale-105 shadow-md"
                  >
                    <CreditCard className="w-5 h-5" strokeWidth={1.5} />
                    Payer l'acompte via Stripe
                  </a>
                  <p className="text-xs text-[#595959]">Le paiement s'ouvrira dans un nouvel onglet. Page securisee par Stripe.</p>
                  <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                    <p className="text-xs text-amber-700 font-medium">La page de paiement ne s'affiche pas correctement ?</p>
                    <p className="text-xs text-amber-600 mt-1">Desactivez temporairement votre bloqueur de publicite (AdBlock) pour le site <strong>checkout.stripe.com</strong>, puis cliquez a nouveau sur le bouton ci-dessus.</p>
                  </div>
                  <button
                    type="button"
                    data-testid="booking-back-to-form-btn"
                    onClick={() => { setCheckoutUrl(null); setFormData({ name: '', email: '', phone: '', message: '', accommodation_type: '', event_type: '', adults: '2', children: '0', guests_reception: '' }); setArrivalDate(null); setDepartureDate(null); setOptions({ bois: false, sono: false, vaisselle: false, cuisine: false, recharge: false, oeufs_qty: 0, selected_extras: [] }); setPricing(null); setCgvAccepted(false); setCancelAccepted(false); setError(''); }}
                    className="text-sm text-[#595959] underline underline-offset-2 hover:text-[#2C3E30] transition-colors mt-2"
                  >
                    Faire une nouvelle demande
                  </button>
                </div>
              )}

              {/* 7. Live pricing breakdown (auto-calculated) */}
              {pricingLoading && (
                <div data-testid="pricing-loading" className="flex items-center gap-2 py-3 text-sm text-[#595959]">
                  <Loader2 className="w-4 h-4 animate-spin text-[#8A9A5B]" /> Calcul du tarif...
                </div>
              )}
              {pricing && !pricingLoading && (
                <div ref={pricingRefCallback} data-testid="pricing-breakdown" className="rounded-xl overflow-hidden border border-[#E5E5E0]">
                  {/* Header */}
                  <div className="bg-[#2C3E30] px-5 py-3">
                    <h4 className="font-semibold text-white text-sm tracking-wide">Estimation en temps reel</h4>
                  </div>
                  {/* Breakdown lines */}
                  <div className="px-5 py-4 space-y-2.5 bg-[#F5F5F0]">
                  {pricing.breakdown.map((item, i) => (
                    <div key={i} className={`flex justify-between text-sm ${item.is_tax ? 'pt-2.5 border-t border-dashed border-[#D5D5D0]' : ''}`} data-testid={`breakdown-line-${i}`}>
                      <span className="text-[#595959]">{item.label}</span>
                      <span className="font-medium text-[#2C3E30] tabular-nums">{fmtEUR(item.amount)}</span>
                    </div>
                  ))}
                  {pricing.tourist_tax > 0 && (
                    <p className="text-[10px] text-[#8A9A5B] italic pt-1">Taxe de sejour 5% du tarif hebergement, plafonnee a 5,39 EUR/pers/nuit (taxe additionnelle departementale incluse). Mineurs exoneres.</p>
                  )}
                  {pricing.breakdown.some(b => b.label && b.label.includes('%')) && (
                    <div className="flex items-center gap-2 py-2 px-3 rounded-lg bg-[#8A9A5B]/10 border border-[#8A9A5B]/20" data-testid="savings-badge">
                      <TrendingDown className="w-3.5 h-3.5 text-[#8A9A5B] flex-shrink-0" />
                      <span className="text-xs text-[#8A9A5B] font-medium">Reduction appliquee sur les nuits/jours supplementaires</span>
                    </div>
                  )}
                  </div>
                  {/* Total & deposit */}
                  <div className="bg-white px-5 py-4 space-y-3 border-t border-[#E5E5E0]">
                    <div className="flex justify-between text-sm">
                      <span className="text-[#595959]">Montant total</span>
                      <span className="font-semibold text-[#2C3E30] tabular-nums" data-testid="pricing-total">{fmtEUR(pricing.total)}</span>
                    </div>
                    <div className="flex justify-between items-baseline pt-2 border-t border-[#E5E5E0]">
                      <span className="font-semibold text-[#2C3E30] text-sm">
                        {pricing.full_payment_required
                          ? 'Paiement integral requis'
                          : `Acompte (30%)${pricing.deposit_refundable === false ? ' non remboursable' : ''}`
                        }
                      </span>
                      <span className="font-bold text-xl text-[#C4A962] tabular-nums" data-testid="pricing-deposit">{fmtEUR(pricing.deposit)}</span>
                    </div>
                  </div>
                  {/* Details */}
                  <div className="bg-[#FAFAF9] px-5 py-3 space-y-1.5 border-t border-[#E5E5E0] text-xs text-[#595959]">
                    {pricing.full_payment_required ? (
                      <p className="text-[#993333] font-medium">
                        La date limite de reglement du solde ({pricing.balance_due_date ? format(new Date(pricing.balance_due_date + 'T00:00:00'), 'd MMMM yyyy', { locale: fr }) : ''}) etant passee, le paiement integral est requis a la reservation.
                      </p>
                    ) : (
                      <p>Solde de {fmtEUR(pricing.total - pricing.deposit)} preleve automatiquement le <strong>{pricing.balance_due_date ? format(new Date(pricing.balance_due_date + 'T00:00:00'), 'd MMMM yyyy', { locale: fr }) : ''}</strong> (selon CGV).</p>
                    )}
                    <p>Caution de {fmtEUR(pricing.caution)} par autorisation bancaire (non debitee).</p>
                    {!pricing.full_payment_required && pricing.deposit_refundable === false && (
                      <p className="text-[#993333] font-medium">En cas d'annulation, l'acompte reste acquis.</p>
                    )}
                    {!pricing.full_payment_required && (
                      <p className="text-[#993333]/70">Annulation automatique si le solde n'est pas regle dans les delais.</p>
                    )}
                    <div className="flex items-center gap-2 pt-1">
                      <Shield className="w-3.5 h-3.5 text-[#8A9A5B]" strokeWidth={1.5} />
                      <span className="text-[#8A9A5B] font-medium">Paiement securise par Stripe</span>
                    </div>
                  </div>
                </div>
              )}

              {/* CGV acceptance checkboxes */}
              {pricing && !pricingLoading && (
                <div data-testid="cgv-acceptance-block" className="space-y-3 p-4 rounded-xl bg-[#FAFAF9] border border-[#E5E5E0]">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="cgv-accept"
                      data-testid="cgv-checkbox"
                      checked={cgvAccepted}
                      onCheckedChange={(v) => setCgvAccepted(!!v)}
                      className="mt-0.5 border-[#C4A962] data-[state=checked]:bg-[#C4A962] data-[state=checked]:border-[#C4A962]"
                    />
                    <label htmlFor="cgv-accept" className="text-sm text-[#595959] leading-relaxed cursor-pointer">
                      J'ai lu et j'accepte les{' '}
                      <a
                        href="/cgv"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="cgv-link"
                        className="text-[#C4A962] underline underline-offset-2 hover:text-[#b5945a] inline-flex items-center gap-1"
                      >
                        <FileText className="w-3 h-3" strokeWidth={1.5} />
                        Conditions Generales de Vente
                      </a>
                    </label>
                  </div>
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="cancel-accept"
                      data-testid="cancel-policy-checkbox"
                      checked={cancelAccepted}
                      onCheckedChange={(v) => setCancelAccepted(!!v)}
                      className="mt-0.5 border-[#C4A962] data-[state=checked]:bg-[#C4A962] data-[state=checked]:border-[#C4A962]"
                    />
                    <label htmlFor="cancel-accept" className="text-sm text-[#595959] leading-relaxed cursor-pointer">
                      Je confirme avoir pris connaissance des conditions d'annulation
                      {pricing.full_payment_required ? (
                        <span className="text-[#993333] font-medium"> (paiement integral)</span>
                      ) : pricing.deposit_refundable === false ? (
                        <span className="text-[#993333] font-medium"> (acompte non remboursable)</span>
                      ) : null}
                    </label>
                  </div>
                </div>
              )}

              {!checkoutUrl && (
              <Button data-testid="booking-submit-btn" type="submit" disabled={loading || !pricing || !cgvAccepted || !cancelAccepted} className={`w-full rounded-full py-6 text-base transition-transform active:scale-95 font-medium ${pricing && cgvAccepted && cancelAccepted ? 'bg-[#C4A962] text-white hover:bg-[#b5945a]' : !pricing ? 'bg-[#8A9A5B] text-white hover:bg-[#7a8a4d]' : 'bg-[#C4A962]/40 text-white cursor-not-allowed'}`} size="lg">
                {loading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Redirection...</>
                ) : pricing ? (
                  <><CreditCard className="w-4 h-4 mr-2" strokeWidth={1.5} /> {pricing.full_payment_required ? `Payer la totalite de ${fmtEUR(pricing.deposit)}` : `Payer l'acompte de ${fmtEUR(pricing.deposit)}`}</>
                ) : (
                  <><Send className="w-4 h-4 mr-2" strokeWidth={1.5} /> Completez le formulaire pour reserver</>
                )}
              </Button>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
    </>
  );
}
